import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog, DialogContent, TextField, List, ListItemButton, ListItemIcon,
  ListItemText, Typography, Chip, Box, CircularProgress, InputAdornment,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SearchIcon from '@mui/icons-material/Search';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PeopleIcon from '@mui/icons-material/People';
import CasinoIcon from '@mui/icons-material/Casino';
import ExploreIcon from '@mui/icons-material/Explore';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CodeIcon from '@mui/icons-material/Code';
import QuizIcon from '@mui/icons-material/Quiz';
import CategoryIcon from '@mui/icons-material/Category';
import WidgetsIcon from '@mui/icons-material/Widgets';
import TuneIcon from '@mui/icons-material/Tune';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';

import axiosClient from '../api/axiosClient';
import { colors, typo } from '../theme/tokens';
import { paths } from '../routes/paths';
import { EmptyState } from './ui';

// Result shape from the SQL function: {type, code, name, version, snippet?}
interface SearchResultItem {
  type: string;
  code: string;
  name: string;
  version?: number;
  // Only set for `component` results: the matched text in context (e.g. the
  // "copyright" string that lives inside the component's data_json body).
  snippet?: string;
}

interface SearchResponse {
  query: string;
  resultCount: number;
  results: SearchResultItem[];
}

const entityRouteMap: Record<string, (tc: string, code: string) => string> = {
  action: (tc, code) => paths.actionEdit(tc, code),
  action_reward: (tc, code) => {
    // code is "taskCode / rewardType", extract task code part
    const taskCode = code.split(' / ')[0] || code;
    return paths.actionEdit(tc, taskCode);
  },
  cohort: (tc) => paths.cohorts(tc),
  sweepstakes: (tc) => paths.sweepstakes(tc),
  adventure: (tc) => paths.adventures(tc),
  // Onboarding flows live under the Settings page's Onboarding tab (index 1).
  flow: (tc) => `${paths.settings(tc)}?tab=1`,
  // Tenant attributes & options live under the Attributes tab (index 2). The
  // sub-tab (?subtab=) picks the editor: 0=Visual Editor, 2=Tenant Attributes,
  // 3=Tenant Options. Default row click opens the Attributes schema form.
  tenant_attribute: (tc) => `${paths.settings(tc)}?tab=2&subtab=2`,
  tenant_option: (tc) => `${paths.settings(tc)}?tab=2&subtab=3`,
  notification: (tc) => paths.notifications(tc),
  // R7: scripts surface moved from tenant app to /admin/scripts. Routing
  // there is a no-op if the user isn't a super-admin (AdminLayout will
  // bounce them); this keeps the existing "search → script" flow useful
  // for operators without reintroducing the tenant-side route.
  script: (_tc) => paths.admin.scripts(),
  trivia: (tc) => paths.trivia(tc),
  // CMS components: jump straight to the component detail page. The result
  // `code` is the component_code, which CmsComponentDetail loads by.
  component: (tc, code) => paths.cmsComponentDetail(tc, code),
};

const entityIconMap: Record<string, React.ReactElement> = {
  action: <PlaylistAddCheckIcon sx={{ fontSize: 20 }} />,
  action_reward: <EmojiEventsIcon sx={{ fontSize: 20 }} />,
  cohort: <PeopleIcon sx={{ fontSize: 20 }} />,
  sweepstakes: <CasinoIcon sx={{ fontSize: 20 }} />,
  adventure: <ExploreIcon sx={{ fontSize: 20 }} />,
  flow: <AccountTreeIcon sx={{ fontSize: 20 }} />,
  notification: <NotificationsIcon sx={{ fontSize: 20 }} />,
  script: <CodeIcon sx={{ fontSize: 20 }} />,
  trivia: <QuizIcon sx={{ fontSize: 20 }} />,
  component: <WidgetsIcon sx={{ fontSize: 20 }} />,
  tenant_attribute: <TuneIcon sx={{ fontSize: 20 }} />,
  tenant_option: <ToggleOnIcon sx={{ fontSize: 20 }} />,
};

const entityColorMap: Record<string, string> = {
  action: '#1565C0',
  action_reward: '#6A1B9A',
  cohort: '#2E7D32',
  sweepstakes: '#C62828',
  adventure: '#E65100',
  flow: '#00695C',
  notification: '#4527A0',
  script: '#37474F',
  trivia: '#AD1457',
  component: '#00838F',
  tenant_attribute: '#455A64',
  tenant_option: '#607D8B',
};

/**
 * Custom hook for global search open state.
 * Call once in Layout and pass [open, setOpen] down.
 * Also registers the Ctrl+K / Cmd+K keyboard shortcut.
 */
export function useGlobalSearchOpen(): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return [open, setOpen];
}

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
}

/** Max recent searches to store */
const MAX_RECENT = 5;
const RECENT_STORAGE_KEY = 'globalSearch_recent';

/** Filter options for scoping search results */
const FILTER_OPTIONS = ['All', 'action', 'cohort', 'sweepstakes', 'notification', 'component', 'flow', 'tenant_attribute', 'tenant_option'] as const;
const FILTER_LABELS: Record<string, string> = {
  'All': 'All',
  'action': 'Actions',
  'cohort': 'Cohorts',
  'sweepstakes': 'Sweepstakes',
  'notification': 'Notifications',
  'component': 'CMS Components',
  'flow': 'Onboarding',
  'tenant_attribute': 'Attributes',
  'tenant_option': 'Options',
};

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ open, onClose }) => {
  const { tenantCode } = useParams<{ tenantCode: string }>();
  const navigate = useNavigate();
  const tc = tenantCode || '';

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('All');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_STORAGE_KEY) || '[]'); } catch { return []; }
  });
  const listRef = useRef<HTMLUListElement>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Reset query and filter when dialog closes
  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebouncedQuery('');
      setActiveFilter('All');
      setHighlightedIndex(-1);
    }
  }, [open]);

  /** Persist a search term to recent searches */
  const addToRecent = useCallback((term: string) => {
    setRecentSearches(prev => {
      const updated = [term, ...prev.filter(s => s !== term)].slice(0, MAX_RECENT);
      try { localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, []);

  const { data, isLoading } = useQuery<SearchResponse>({
    queryKey: ['global-search', tc, debouncedQuery],
    queryFn: () =>
      axiosClient.get(`/api/search?tenant=${tc}&q=${encodeURIComponent(debouncedQuery)}`).then(r => r.data),
    enabled: open && !!tc && debouncedQuery.length >= 2,
  });

  // Group results by entity type
  const grouped = useMemo(() => {
    if (!data?.results || !Array.isArray(data.results)) return {};
    const filtered = activeFilter === 'All' ? data.results : data.results.filter(r => r.type === activeFilter);
    return filtered.reduce<Record<string, SearchResultItem[]>>((acc, item) => {
      const entityType = item.type;
      if (!entityType) return acc;
      if (!acc[entityType]) acc[entityType] = [];
      acc[entityType].push(item);
      return acc;
    }, {});
  }, [data, activeFilter]);

  /** Counts per entity type (unfiltered) for the filter tabs */
  const typeCounts = useMemo(() => {
    if (!data?.results || !Array.isArray(data.results)) return {};
    return data.results.reduce<Record<string, number>>((acc, item) => {
      if (item.type) acc[item.type] = (acc[item.type] || 0) + 1;
      return acc;
    }, {});
  }, [data]);

  /** Flat list of all currently visible results for keyboard navigation */
  const flatResults = useMemo(() => {
    return Object.values(grouped).flat();
  }, [grouped]);

  const handleSelect = useCallback(
    (result: SearchResultItem) => {
      if (debouncedQuery) addToRecent(debouncedQuery);
      const routeFn = entityRouteMap[result.type];
      if (routeFn) {
        let path = routeFn(tc, result.code);
        // Carry the query as find= so the destination jumps to the matching
        // field (settings attribute/option form, or CMS component data view).
        if (['tenant_attribute', 'tenant_option', 'component'].includes(result.type) && debouncedQuery) {
          path += `${path.includes('?') ? '&' : '?'}find=${encodeURIComponent(debouncedQuery)}`;
        }
        navigate(path);
      }
      onClose();
    },
    [navigate, tc, onClose, debouncedQuery, addToRecent],
  );

  /** Navigate to an explicit path (used by per-result action shortcuts). */
  const goTo = useCallback(
    (path: string) => {
      if (debouncedQuery) addToRecent(debouncedQuery);
      navigate(path);
      onClose();
    },
    [navigate, onClose, debouncedQuery, addToRecent],
  );

  /** Keyboard navigation handler */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && highlightedIndex >= 0 && flatResults[highlightedIndex]) {
      e.preventDefault();
      handleSelect(flatResults[highlightedIndex]);
    }
  }, [flatResults, highlightedIndex, handleSelect]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // Global search is a READ affordance and must stay usable on read-only
      // envs. The dialog portals into document.body (outside the TopBar chrome),
      // so mark the Paper as read-only-interactive — otherwise ReadOnlyGuard's
      // document-wide `beforeinput` block swallows typing into the search box.
      PaperProps={{
        'data-readonly-interactive': 'true',
        sx: {
          borderRadius: '12px',
          border: `1px solid ${colors.borderLight}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          maxHeight: '70vh',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }} onKeyDown={handleKeyDown}>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search actions, cohorts, components, settings..."
          value={query}
          onChange={(e) => { setQuery(e.target.value); setHighlightedIndex(-1); }}
          variant="outlined"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.textSecondary }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Chip
                    label={navigator.platform.includes('Mac') ? '\u2318K' : 'Ctrl+K'}
                    size="small"
                    sx={{ bgcolor: colors.bgMuted, color: colors.textSecondary, fontFamily: 'Manrope, sans-serif', fontSize: 11 }}
                  />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontFamily: 'Manrope, sans-serif', fontSize: 15,
              '& fieldset': { border: 'none', borderBottom: `1px solid ${colors.borderLight}` },
            },
            '& .MuiOutlinedInput-input': { py: 2, px: 1 },
          }}
        />

        {/* ── Filter tabs (only when results exist) ── */}
        {!isLoading && data?.results && data.results.length > 0 && (
          <Box sx={{
            display: 'flex', gap: 0.5, px: 2, py: 1, borderBottom: `1px solid ${colors.borderLight}`,
            overflowX: 'auto',
          }}>
            {FILTER_OPTIONS.filter(f => f === 'All' || (typeCounts[f] ?? 0) > 0).map(filter => {
              const count = filter === 'All' ? data.results.length : (typeCounts[filter] ?? 0);
              const isActive = activeFilter === filter;
              return (
                <Chip
                  key={filter}
                  label={`${FILTER_LABELS[filter] || filter}${count > 0 ? ` (${count})` : ''}`}
                  size="small"
                  onClick={() => { setActiveFilter(filter); setHighlightedIndex(-1); }}
                  sx={{
                    fontSize: 11, fontWeight: isActive ? 700 : 500, fontFamily: 'Manrope, sans-serif',
                    cursor: 'pointer',
                    backgroundColor: isActive ? colors.brand : colors.bgMuted,
                    color: isActive ? colors.textPrimary : colors.textSecondary,
                    '&:hover': { backgroundColor: isActive ? colors.brandHover : colors.bgSubtle },
                  }}
                />
              );
            })}
          </Box>
        )}

        {isLoading && debouncedQuery.length >= 2 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: colors.brand }} />
          </Box>
        )}

        {!isLoading && debouncedQuery.length >= 2 && (!data?.results || data.results.length === 0) && (
          <EmptyState
            variant="search"
            title={`No results found for "${debouncedQuery}"`}
            description="Try a different search term or check the spelling."
          />
        )}

        {!isLoading && Object.keys(grouped).length > 0 && (() => {
          let flatIdx = -1;
          return (
            <List ref={listRef} disablePadding sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {Object.entries(grouped).map(([type, results]) => (
                <React.Fragment key={type}>
                  <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={typo.sectionLabel}>
                      {type.replace(/_/g, ' ')}
                    </Typography>
                    <Chip label={results.length} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, fontFamily: 'Manrope, sans-serif', backgroundColor: `${entityColorMap[type] || colors.textSecondary}14`, color: entityColorMap[type] || colors.textSecondary }} />
                  </Box>
                  {results.map((result, idx) => {
                    flatIdx++;
                    const currentFlatIdx = flatIdx;
                    return (
                      <ListItemButton
                        key={`${result.code}-${idx}`}
                        selected={highlightedIndex === currentFlatIdx}
                        onClick={() => handleSelect(result)}
                        sx={{
                          px: 2, py: 1,
                          '&:hover': { bgcolor: colors.brandLight },
                          '&.Mui-selected': { bgcolor: colors.brandLight, '&:hover': { bgcolor: colors.brandLight } },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: entityColorMap[type] || colors.textSecondary }}>
                          {entityIconMap[type] || <CategoryIcon sx={{ fontSize: 20 }} />}
                        </ListItemIcon>
                        <ListItemText
                          primary={result.name || result.code}
                          secondary={
                            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography component="span" sx={{ ...typo.mono, fontSize: 12, color: colors.textSecondary }}>{result.code}</Typography>
                                {result.version != null && (
                                  <Chip label={`v${result.version}`} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, fontFamily: 'Manrope, sans-serif', backgroundColor: colors.bgMuted, color: colors.textTertiary }} />
                                )}
                              </Box>
                              {result.snippet && (
                                <Typography component="span" sx={{ fontSize: 11, color: colors.textTertiary, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360 }}>
                                  …{result.snippet}…
                                </Typography>
                              )}
                            </Box>
                          }
                          primaryTypographyProps={{ fontFamily: 'Manrope, sans-serif', fontSize: 14, fontWeight: 500, color: colors.textPrimary }}
                        />
                        {type === 'tenant_attribute' ? (
                          // Tenant attributes are editable two ways — offer both.
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {([
                              { label: 'Attributes', subtab: 2 },
                              { label: 'Visual Editor', subtab: 0 },
                            ] as const).map((a) => (
                              <Chip
                                key={a.label}
                                label={a.label}
                                size="small"
                                clickable
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Seed the field search only for the schema form (subtab 2);
                                  // the Visual Editor isn't a field list.
                                  const find = a.subtab === 2 && debouncedQuery ? `&find=${encodeURIComponent(debouncedQuery)}` : '';
                                  goTo(`${paths.settings(tc)}?tab=2&subtab=${a.subtab}${find}`);
                                }}
                                sx={{
                                  bgcolor: `${entityColorMap[type] || colors.textSecondary}14`,
                                  color: entityColorMap[type] || colors.textSecondary,
                                  fontFamily: 'Manrope, sans-serif', fontSize: 11, fontWeight: 600,
                                  '&:hover': { bgcolor: `${entityColorMap[type] || colors.textSecondary}29` },
                                }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Chip
                            label={type.replace(/_/g, ' ')}
                            size="small"
                            sx={{
                              bgcolor: `${entityColorMap[type] || colors.textSecondary}14`,
                              color: entityColorMap[type] || colors.textSecondary,
                              fontFamily: 'Manrope, sans-serif', fontSize: 11, fontWeight: 600,
                            }}
                          />
                        )}
                      </ListItemButton>
                    );
                  })}
                </React.Fragment>
              ))}
            </List>
          );
        })()}

        {/* ── Idle state: recent searches + keyboard hints ── */}
        {debouncedQuery.length < 2 && !isLoading && (
          <Box sx={{ py: 3, px: 2 }}>
            {/* Recent searches */}
            {recentSearches.length > 0 && (
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ ...typo.sectionLabel, mb: 1, px: 0 }}>Recent Searches</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {recentSearches.map((term, i) => (
                    <ListItemButton
                      key={i}
                      onClick={() => { setQuery(term); setDebouncedQuery(term); }}
                      sx={{ borderRadius: '6px', py: 0.75, px: 1, '&:hover': { bgcolor: colors.brandLight } }}
                    >
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <AccessTimeIcon sx={{ fontSize: 16, color: colors.textTertiary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={term}
                        primaryTypographyProps={{ fontFamily: 'Manrope, sans-serif', fontSize: 13, color: colors.textSecondary }}
                      />
                    </ListItemButton>
                  ))}
                </Box>
              </Box>
            )}

            {/* Search hint */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography sx={typo.secondaryText}>
                Type at least 2 characters to search
              </Typography>
            </Box>

            {/* Keyboard navigation hints */}
            <Box sx={{
              display: 'flex', justifyContent: 'center', gap: 2.5,
              pt: 1.5, borderTop: `1px solid ${colors.borderLight}`,
            }}>
              {[
                { keys: [<KeyboardArrowUpIcon key="up" sx={{ fontSize: 12 }} />, <KeyboardArrowDownIcon key="down" sx={{ fontSize: 12 }} />], label: 'Navigate' },
                { keys: [<KeyboardReturnIcon key="enter" sx={{ fontSize: 12 }} />], label: 'Select' },
                { keys: [<Typography key="esc" sx={{ fontSize: 10, fontWeight: 700 }}>ESC</Typography>], label: 'Close' },
              ].map((hint, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {hint.keys.map((k, ki) => (
                    <Box key={ki} sx={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      px: 0.5, py: 0.25, borderRadius: '4px', minWidth: 20,
                      backgroundColor: colors.bgMuted, border: `1px solid ${colors.borderLight}`,
                    }}>
                      {k}
                    </Box>
                  ))}
                  <Typography sx={{ fontSize: 11, color: colors.textTertiary }}>{hint.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
